import i18next from 'i18next';
import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';
import {
    fetchOrderProductDetailsCall,
    getOrderFilter,
    getOrdersByFilter,
    getOrdersByFilterId,
    saveBillingAddressOrder,
    saveDeliveryAddressOrder,
    saveOrder,
} from 'src/api/admin-orders';
import { generateNewPageable, isPageTooHigh } from '../../adminPanel/utils/helperClasses';
import { enqueueSnackbarAction } from '../notifications/actions';
import {
    CLEAR_ORDER_PRODUCT_DETAILS,
    fetchOrderProductDetails,
    fetchOrdersByFilter,
    fetchOrdersByFilterId,
    FETCH_ORDERS_BY_FILTER,
    FETCH_ORDERS_BY_FILTER_ID,
    FETCH_ORDER_FILTER,
    FETCH_ORDER_PRODUCT_DETAILS,
    requestClearOrderProductDetails,
    requestOrderFilter,
    requestOrderFilterFailure,
    requestOrderFilterSuccess,
    requestOrderProductDetails,
    requestOrderProductDetailsFailure,
    requestOrderProductDetailsSuccess,
    requestOrdersByFilter,
    requestOrdersByFilterFailure,
    requestOrdersByFilterId,
    requestOrdersByFilterIdFailure,
    requestOrdersByFilterIdSuccess,
    requestOrdersByFilterSuccess,
    requestUpdateBillingAddressOrder,
    requestUpdateBillingAddressOrderFailure,
    requestUpdateBillingAddressOrderSuccess,
    requestUpdateDeliveryAddressOrder,
    requestUpdateDeliveryAddressOrderFailure,
    requestUpdateDeliveryAddressOrderSuccess,
    requestUpdateOrder,
    requestUpdateOrderFailure,
    requestUpdateOrderSuccess,
    UPDATE_BILLING_ADDRESS_ORDER,
    UPDATE_DELIVERY_ADDRESS_ORDER,
    UPDATE_ORDER,
} from './actions';

function* fetchOrderFilterSaga(): SagaIterator {
    yield put(requestOrderFilter());

    const { error, response } = yield call(getOrderFilter);

    if (error) {
        yield put(requestOrderFilterFailure(error));
    } else {
        yield put(requestOrderFilterSuccess(response));
    }
}

export function* generateFetchOrderFilterWatcher() {
    yield takeLatest(FETCH_ORDER_FILTER, fetchOrderFilterSaga);
}

function* fetchOrdersByFilterSaga(action: AnyAction): SagaIterator {
    yield put(requestOrdersByFilter());

    const { error, response } = yield call(getOrdersByFilter, action.pageable, action.filter);

    if (error) {
        yield put(requestOrdersByFilterFailure(error));
    } else {
        if (isPageTooHigh(response.data)) {
            yield put(fetchOrdersByFilter(action.filter, generateNewPageable(action.pageable)));
        } else {
            yield put(requestOrdersByFilterSuccess(response));
        }
    }
}

export function* generateFetchOrdersByFilterWatcher() {
    yield takeLatest(FETCH_ORDERS_BY_FILTER, fetchOrdersByFilterSaga);
}

function* fetchOrdersByFilterIdSaga(action: AnyAction): SagaIterator {
    yield put(requestOrdersByFilterId());

    const { error, response } = yield call(getOrdersByFilterId, action.filterId, action.pageable);

    if (error) {
        yield put(requestOrdersByFilterIdFailure(error));
    } else {
        if (isPageTooHigh(response.data)) {
            yield put(fetchOrdersByFilterId(action.filterId, generateNewPageable(action.pageable)));
        } else {
            yield put(requestOrdersByFilterIdSuccess(response));
        }
    }
}

export function* generateFetchOrdersByFilterIdWatcher() {
    yield takeLatest(FETCH_ORDERS_BY_FILTER_ID, fetchOrdersByFilterIdSaga);
}

function* fetchOrderProductDetailsSaga(action: AnyAction): SagaIterator {
    yield put(requestOrderProductDetails());

    const { error, response } = yield call(fetchOrderProductDetailsCall, action.publicId);

    if (error) {
        yield put(requestOrderProductDetailsFailure(error));
    } else {
        yield put(requestOrderProductDetailsSuccess(response));
    }
}

export function* generateFetchOrderProductDetailsWatcher() {
    yield takeLatest(FETCH_ORDER_PRODUCT_DETAILS, fetchOrderProductDetailsSaga);
}

function* clearOrderProductDetailsSaga(action: AnyAction): SagaIterator {
    yield put(requestClearOrderProductDetails());
}

export function* generateClearOrderProductDetailsWatcher() {
    yield takeLatest(CLEAR_ORDER_PRODUCT_DETAILS, clearOrderProductDetailsSaga);
}

function* updateOrderSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateOrder());

    const { error, response } = yield call(saveOrder, action.publicId, action.order, action.additionalParams);

    if (error) {
        yield put(requestUpdateOrderFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('offsetting:pages.order.details.message.updateOrder.error'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'error',
                },
            })
        );
    } else {
        yield put(requestUpdateOrderSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: i18next.t('offsetting:pages.order.details.message.updateOrder.success'),
                options: {
                    key: new Date().getTime() + Math.random(),
                    variant: 'success',
                },
            })
        );
        yield put(fetchOrderProductDetails(action.publicId));
    }
}

export function* generateUpdateOrderWatcher() {
    yield takeLatest(UPDATE_ORDER, updateOrderSaga);
}

function* updateBillingAddressOrderSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateBillingAddressOrder());

    const { error, response } = yield call(saveBillingAddressOrder, action.publicId, action.address);

    if (error) {
        yield put(requestUpdateBillingAddressOrderFailure(error));
    } else {
        yield put(requestUpdateBillingAddressOrderSuccess(response));
    }
}

export function* generateUpdateBillingAddressOrderWatcher() {
    yield takeLatest(UPDATE_BILLING_ADDRESS_ORDER, updateBillingAddressOrderSaga);
}

function* updateDeliveryAddressOrderSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateDeliveryAddressOrder());

    const { error, response } = yield call(saveDeliveryAddressOrder, action.publicId, action.address);

    if (error) {
        yield put(requestUpdateDeliveryAddressOrderFailure(error));
    } else {
        yield put(requestUpdateDeliveryAddressOrderSuccess(response));
    }
}

export function* generateUpdateDeliveryAddressOrderWatcher() {
    yield takeLatest(UPDATE_DELIVERY_ADDRESS_ORDER, updateDeliveryAddressOrderSaga);
}
